import { SSE, type SSEvent } from 'sse.js'

export interface QueryParams {
    [key: string]: string | number | boolean | undefined | null
}

class API {
    async get<T = unknown>(url: string, query?: QueryParams, headers?: HeadersInit): Promise<T> {
        try {
            const queryString = this.parseQuery(query)
            const response = await fetch(`${url}${queryString}`, {
                headers: headers,
            })
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }
            const data = await response.json()
            return data
        } catch (error) {
            if (error instanceof TypeError) {
                const corsError = new Error(
                    `[API] CORS error or network issue: ${error.message}`
                )
                return Promise.reject(corsError)
            } else {
                console.error('[API] error', error)
                const errorObj = error instanceof Error ? error : new Error(String(error))
                return Promise.reject(errorObj)
            }
        }
    }

    async post<T = unknown>(
        url: string,
        query?: QueryParams,
        body?: BodyInit,
        headers?: HeadersInit
    ): Promise<T> {
        const queryString = this.parseQuery(query)

        const response = await fetch(`${url}${queryString}`, {
            method: 'POST',
            headers: headers,
            body: body,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                return response.json()
            })
            .then((data) => {
                return data
            })
            .catch((error) => {
                if (error instanceof TypeError) {
                    const corsError = new Error(
                        `[API] CORS error or network issue: ${error.message}`
                    )
                    return Promise.reject(corsError)
                } else {
                    console.error('[API] error', error)
                    return error instanceof Error ? error : new Error(String(error))
                }
            })

        return response
    }

    async patch<T = unknown>(
        url: string,
        query?: QueryParams,
        body?: BodyInit,
        headers?: HeadersInit
    ): Promise<T> {
        const queryString = this.parseQuery(query)

        const response = await fetch(`${url}${queryString}`, {
            method: 'PATCH',
            headers: headers,
            body: body,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                return response.json()
            })
            .then((data) => {
                return data
            })
            .catch((error) => {
                if (error instanceof TypeError) {
                    const corsError = new Error(
                        `[API] CORS error or network issue: ${error.message}`
                    )
                    return Promise.reject(corsError)
                } else {
                    console.error('[API] error', error)
                    return error instanceof Error ? error : new Error(String(error))
                }
            })

        return response
    }

    async postSSE(
        url: string,
        query?: QueryParams,
        body?: string,
        headers?: Record<string, string>
    ): Promise<SSE> {
        const queryString = this.parseQuery(query)

        const sseHeaders = {
            ...headers,
            Accept: 'text/event-stream',
        }

        return new Promise((resolve, reject) => {
            const sse = new SSE(`${url}${queryString}`, {
                method: 'POST',
                payload: body,
                headers: sseHeaders,
            })

            sse.addEventListener('error', (event: SSEvent) => {
                console.warn('error in sse post')
                console.error('error', event)
                reject(new Error('SSE error occurred'))
            })

            resolve(sse)
        })
    }

    async rawFetch(url: string, options?: RequestInit, query?: QueryParams): Promise<Response> {
        const queryString = this.parseQuery(query)

        const response = await fetch(`${url}${queryString}`, options)

        return response
    }

    /************************************************************************
     *                           helper functions
     ************************************************************************/

    parseQuery = (query?: QueryParams): string => {
        if (!query) return ''

        let queryString = '?'

        for (const queryKey in query) {
            const value = query[queryKey]
            if (!value) {
                continue
            }
            queryString += `${queryKey}=${value}&`
        }

        return queryString
    }
}

export default new API()
